import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";

import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";

import { actionCreators as actionCreatorsAdmin } from "../../../../_actions/adminActions";
import Loading from "../../../common/Loading";
import EnhancedTableHead, {
  stableSort,
  getComparator
} from "../../../common/EnhancedTableHead";

import HelpTextEditDialog from "./EditDialog";
import { PAGE_SIZE } from "../../../../constants";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  cursorPointer: {
    cursor: "pointer"
  }
}));

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: "Topic"
  },
  {
    id: "updatedDate",
    numeric: true,
    disablePadding: false,
    label: "Updated Date"
  },
  {
    id: "updatedByUserName",
    numeric: true,
    disablePadding: false,
    label: "Submitted By"
  }
];

const sortBy = {
  name: 1,
  updatedDate: 2,
  updatedByUserName: 3
};

const ManageHelpText = props => {
  const classes = useStyles();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(localStorage.getItem("helpTextPageSize")) || PAGE_SIZE
  );
  const [editHelpText, setEditHelpText] = useState(null);

  const {
    getHelpTexts,
    updateHelpText,
    helpTexts,
    totalLength,
    isFetching,
    isFetched
  } = props;

  const fetchHelpTexts = useCallback(() => {
    getHelpTexts(100, 0, sortBy[orderBy], order);
  }, [rowsPerPage, page, orderBy, order, helpTexts]);

  useEffect(() => {
    localStorage.setItem("helpTextPageSize", rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    if (!isFetching.HELP_TEXT && !isFetched.HELP_TEXT) {
      fetchHelpTexts();
    }
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleEditTopic = useCallback(
    row => () => {
      setEditHelpText(row);
    },
    []
  );

  const handleTopicSave = useCallback(
    async text => {
      await updateHelpText({
        text,
        id: editHelpText.id
      });
      setEditHelpText(null);
      await fetchHelpTexts();
    },
    [editHelpText, fetchHelpTexts]
  );

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    localStorage.setItem("helpTextPageSize", value);
    setPage(0);
  }, []);

  const paginate = useCallback(
    list => {
      return list.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    },
    [page, rowsPerPage]
  );

  const getStandardDate = useCallback(dateString => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US");
  }, []);

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">Manage Help Text</Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Paper className={classes.paper}>
            <TableContainer>
              <Table
                className={classes.table}
                aria-labelledby="tableTitle"
                size="medium"
                aria-label="enhanced table"
              >
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={totalLength}
                  headCells={headCells}
                />

                <TableBody>
                  {isFetching.HELP_TEXT ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Loading />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginate(
                      stableSort(helpTexts, getComparator(order, orderBy)).map(
                        (row, index) => {
                          const labelId = `enhanced-table-row-${index}`;

                          return (
                            <TableRow
                              hover
                              className={classes.cursorPointer}
                              onClick={handleEditTopic(row)}
                              role="checkbox"
                              tabIndex={-1}
                              key={labelId}
                            >
                              <TableCell
                                component="th"
                                scope="row"
                                padding="none"
                              >
                                {row.name}
                              </TableCell>
                              <TableCell align="right">
                                {getStandardDate(row.updatedDate)}
                              </TableCell>
                              <TableCell align="right">
                                {row.updatedByUserName}
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Paper>
        </CardBody>
      </Card>
      <HelpTextEditDialog
        open={!!editHelpText}
        topic={editHelpText ? editHelpText.name : ""}
        content={editHelpText ? editHelpText.text : ""}
        onClose={handleEditTopic(null)}
        onSave={handleTopicSave}
      />
    </Container>
  );
};

const mapStateToProps = state => ({
  isLoading: state.adminReducer.isLoading,
  helpTexts: state.adminReducer.helpTexts,
  totalLength: state.adminReducer.total,
  isFetching: state.adminReducer.isFetching,
  isFetched: state.adminReducer.isFetched
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ManageHelpText);
